import { useMemo } from 'react';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { RemoveExtention } from 'utils/strings/string.utils';

const SIREN_DEVICE_TYPE = 'Technology Bridge_Siren/Giant Voice';

const normalizeType = (value = '') => `${value}`.toLowerCase().replace(/[\s_/-]/g, '');

const isSirenDeviceType = (value = '') => {
  const normalized = normalizeType(value);
  return normalized.includes('siren') && normalized.includes('giantvoice');
};

/**
 * The files a GROUP can play as one: universal files, plus files uploaded to
 * every siren in `sirenIds`. Mike, 2026-08-28: the group window said "No
 * Audio Available" because only universal files were offered, and every file
 * in the library is uploaded to specific sirens.
 */
export function filesOnEverySiren(allFiles, sirenIds) {
  const ids = (sirenIds || []).map((id) => `${id}`);
  if (ids.length === 0) return [];
  return (allFiles || []).filter((file) => {
    if (!file) return false;
    if (!Array.isArray(file.device_ids) || file.device_ids.length === 0) return true; // universal
    const on = new Set(file.device_ids.map((id) => `${id}`));
    return ids.every((id) => on.has(id));
  });
}

export default function useSirenAudio(devices) {
  const { data: deviceTypes } = useGetAllAvailableDeviceTypesQuery();

  // deviceTypes["RescAlert Devices"]["Technology Bridge_Siren/Giant Voice"].sirenTypes
  const sirenTypes = useMemo(() => {
    const rescAlertTypes = deviceTypes?.['RescAlert Devices'];
    if (!rescAlertTypes || typeof rescAlertTypes !== 'object') return [];

    const exactTypes = rescAlertTypes?.[SIREN_DEVICE_TYPE]?.sirenTypes;
    if (Array.isArray(exactTypes)) return exactTypes;

    const fallbackKey = Object.keys(rescAlertTypes).find((typeKey) => isSirenDeviceType(typeKey));
    const fallbackTypes = fallbackKey ? rescAlertTypes?.[fallbackKey]?.sirenTypes : null;
    return Array.isArray(fallbackTypes) ? fallbackTypes : [];
  }, [deviceTypes]);

  const filteredDevices = useMemo(() => {
    if (!devices) return [];

    if (!Array.isArray(devices)) {
      return isSirenDeviceType(devices?.device_type) ? [devices] : [];
    }

    return devices.filter((d) => isSirenDeviceType(d?.device_type));
  }, [devices]);

  const { allAudio, universalAudio, sirenAudio, groupAudio } = useMemo(() => {
    if (!Array.isArray(sirenTypes)) {
      return { allAudio: [], universalAudio: [], sirenAudio: [], groupAudio: [] };
    }

    const allFiles = sirenTypes.map((audio) => {
      if (!audio) return audio;

      const name = audio.name ? RemoveExtention(audio.name) : audio.name;
      const displayName = audio.displayName ? RemoveExtention(audio.displayName) : name;

      return {
        ...audio,
        name,
        displayName,
      };
    });

    const deviceIdSet = new Set(filteredDevices.map((device) => `${device?.id}`));

    const universal = allFiles.filter(
      (file) => !Array.isArray(file?.device_ids) || file.device_ids.length === 0,
    );

    const targeted = allFiles.filter((file) => {
      if (!Array.isArray(file?.device_ids) || file.device_ids.length === 0) return false;
      if (!deviceIdSet.size) return false;

      return file.device_ids.some((id) => deviceIdSet.has(`${id}`));
    });

    return {
      allAudio: allFiles,
      universalAudio: universal,
      sirenAudio: targeted,
      // What the whole group can play as one: universal, or on every siren given.
      groupAudio: filesOnEverySiren(allFiles, filteredDevices.map((device) => device?.id)),
    };
  }, [filteredDevices, sirenTypes]);

  return { allAudio, sirenAudio, universalAudio, groupAudio };
}
